/**
 * Pure date validation helpers (no React, no store, no side effects).
 */

/** Minimum age, in years, allowed for an employee. */
export const MIN_EMPLOYEE_AGE = 18;

/**
 * Validates an employee date of birth.
 *
 * Rules:
 * - Empty value is valid (the field may be optional).
 * - A date today or in the future is invalid.
 * - The employee must be at least `MIN_EMPLOYEE_AGE` years old today.
 *
 * Both dates are normalized to midnight so the comparison is day-granular.
 *
 * @returns An error message, or an empty string when the value is valid.
 */
export const validateDob = (dob?: string): string => {
  if (!dob) return "";

  const selected = new Date(dob);
  if (Number.isNaN(selected.getTime())) return "Enter a valid date of birth";

  const today = new Date();

  selected.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const minDob = new Date(today);
  minDob.setFullYear(today.getFullYear() - MIN_EMPLOYEE_AGE);

  if (selected >= today) {
    return "Date of birth cannot be today or a future date";
  }

  if (selected > minDob) {
    return `Employee must be at least ${MIN_EMPLOYEE_AGE} years old`;
  }

  return "";
};
